// src/components/ProductIncomingManagementPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaPrint, FaSearch, FaFilter, FaCalendarAlt } from "react-icons/fa";
import "./ProductIncoming.css";

const API_URL = process.env.REACT_APP_API_BASE_URL || "https://sipedasapi.bkkpangkalpinang.com/api";

// helper: "YYYY-MM-DD" dari value tanggal (untuk dicocokkan dengan input type=date)
const toISODate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const getYear = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.getFullYear();
};

export default function ProductIncomingManagementPage() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // pagination dari Laravel paginate()
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  // UI filter/search
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");

  // NEW: filter tanggal & tahun
  const [tanggal, setTanggal] = useState(""); // format: YYYY-MM-DD
  const [tahun, setTahun] = useState("all"); // "all" atau angka string

  const sourceLabel = (src) => {
    if (src === "pembelian") return "Pembelian";
    if (src === "transfer_masuk") return "Transfer Masuk";
    return src || "-";
  };

  const fmtDate = (v) => {
    if (!v) return "-";
    try {
      return new Date(v).toLocaleDateString("id-ID");
    } catch {
      return v;
    }
  };

  const load = async (targetPage = 1) => {
    setLoading(true);
    setErr("");
    try {
      // ✅ route backend kamu: /incoming_products (underscore)
      const res = await axios.get(`${API_URL}/incoming_products`, {
        params: { page: targetPage },
      });

      const data = res?.data?.data ?? [];
      setRows(Array.isArray(data) ? data : []);

      setPage(res?.data?.current_page ?? targetPage);
      setLastPage(res?.data?.last_page ?? 1);
      setTotal(res?.data?.total ?? (Array.isArray(data) ? data.length : 0));
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Gagal memuat data produk masuk.");
      setRows([]);
      setPage(1);
      setLastPage(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, []);

  // pilihan tahun (diambil dari rows yang sedang tampil)
  const yearOptions = useMemo(() => {
    const s = new Set();
    (rows || []).forEach((r) => {
      const y = getYear(r.tanggal_masuk) ?? getYear(r.created_at);
      if (y) s.add(y);
    });
    return Array.from(s).sort((a, b) => b - a);
  }, [rows]);

  // client-side filter biar responsif
  const filtered = useMemo(() => {
    const keyword = q.trim().toLowerCase();

    return (rows || []).filter((r) => {
      const p = r.product || {};
      const nama = String(p.name ?? p.nama_produk ?? p.nama ?? "").toLowerCase();
      const id = String(p.id ?? r.product_id ?? "").toLowerCase();
      const s = String(r.source ?? "").toLowerCase();

      // sumber
      const matchSource = source === "all" ? true : s === source;

      // search ID/nama
      const matchQ = !keyword ? true : nama.includes(keyword) || id.includes(keyword);

      // tanggal masuk
      const isoMasuk = toISODate(r.tanggal_masuk) || toISODate(r.created_at);
      const matchTanggal = !tanggal ? true : isoMasuk === tanggal;

      // tahun masuk
      const y = getYear(r.tanggal_masuk) ?? getYear(r.created_at);
      const matchTahun = tahun === "all" ? true : String(y) === String(tahun);

      return matchSource && matchQ && matchTanggal && matchTahun;
    });
  }, [rows, q, source, tanggal, tahun]);

  const handleResetFilter = () => {
    setQ("");
    setSource("all");
    setTanggal("");
    setTahun("all");
  };

  const handlePrintAll = async () => {
    try {
      const res = await axios.get(`${API_URL}/incoming_products`, { params: { all: 1 } });
      const data = res?.data?.data ?? [];

      const html = `
        <html>
        <head>
          <title>Cetak Data Produk Masuk</title>
          <style>
            body{ font-family: Arial, sans-serif; padding: 16px; }
            h2{ margin: 0 0 12px; }
            table{ width:100%; border-collapse: collapse; }
            th, td{ border:1px solid #ccc; padding:8px; font-size:12px; vertical-align: top; }
            th{ background:#f3f4f6; text-align:left; }
          </style>
        </head>
        <body>
          <h2>Data Produk Masuk</h2>
          <table>
            <thead>
              <tr>
                <th>Id Produk</th>
                <th>Nama Produk</th>
                <th>Sumber</th>
                <th>Keterangan sumber</th>
                <th>Pabrik</th>
                <th>Tanggal Masuk</th>
                <th>Batch</th>
                <th>Stok Masuk</th>
                <th>Tanggal Kadaluarsa</th>
                <th>Keterangan</th>
              </tr>
            </thead>
            <tbody>
              ${
                data.length
                  ? data
                      .map((r) => {
                        const p = r.product || {};
                        return `
                          <tr>
                            <td>${p.id ?? r.product_id ?? "-"}</td>
                            <td>${p.name ?? p.nama_produk ?? p.nama ?? "-"}</td>
                            <td>${sourceLabel(r.source)}</td>
                            <td>${r.source_note ?? "-"}</td>
                            <td>${r.pabrik ?? "-"}</td>
                            <td>${fmtDate(r.tanggal_masuk)}</td>
                            <td>${r.batch ?? "-"}</td>
                            <td>${r.stok_masuk ?? "-"}</td>
                            <td>${fmtDate(r.tanggal_kadaluarsa)}</td>
                            <td>${r.keterangan ?? "-"}</td>
                          </tr>
                        `;
                      })
                      .join("")
                  : `<tr><td colspan="10" style="text-align:center;">Belum ada data.</td></tr>`
              }
            </tbody>
          </table>
          <script>window.onload=function(){window.print();}</script>
        </body>
        </html>
      `;

      const w = window.open("", "_blank");
      if (!w) return alert("Popup diblokir browser. Izinkan popup untuk mencetak.");
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Gagal cetak data produk masuk.");
    }
  };

  return (
    <div className="pi-page">
      {/* Header + Actions */}
      <div className="pi-head">
        <div>
          <h2 className="pi-title">Produk Masuk</h2>
          <div className="pi-subtitle">
            Total data: {total}{" "}
            <span style={{ opacity: 0.7 }}>• Tampil: {filtered.length}</span>
          </div>
        </div>

        <div className="pi-actions">
          <button type="button" className="pi-btn pi-btn-outline" onClick={handlePrintAll}>
            <FaPrint /> CETAK DATA PRODUK MASUK
          </button>

          <button
            type="button"
            className="pi-btn pi-btn-primary"
            onClick={() => navigate("/admin/produk-masuk/input")}
          >
            <FaPlus /> INPUT PRODUK MASUK
          </button>
        </div>
      </div>

      {/* Toolbar: search + filter */}
      <div className="pi-toolbar">
        <div className="pi-search">
          <FaSearch className="pi-ic" />
          <input
            className="pi-input"
            placeholder="Cari ID / Nama Produk..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {/* Filter sumber */}
        <div className="pi-filter">
          <FaFilter className="pi-ic" />
          <select className="pi-select" value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="all">Semua Sumber</option>
            <option value="pembelian">Pembelian</option>
            <option value="transfer_masuk">Transfer Masuk</option>
          </select>
        </div>

        {/* Filter tanggal */}
        <div className="pi-filter">
          <FaCalendarAlt className="pi-ic" />
          <input
            className="pi-select"
            type="date"
            value={tanggal}
            onChange={(e) => setTanggal(e.target.value)}
            title="Filter Tanggal Masuk"
          />
        </div>

        {/* Filter tahun */}
        <div className="pi-filter">
          <FaCalendarAlt className="pi-ic" />
          <select className="pi-select" value={tahun} onChange={(e) => setTahun(e.target.value)}>
            <option value="all">Semua Tahun</option>
            {yearOptions.map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <button type="button" className="pi-btn pi-btn-light" onClick={handleResetFilter}>
          Reset
        </button>
      </div>

      {err && <div className="pi-alert">{err}</div>}

      {/* Card Table */}
      <div className="pi-card">
        <div className="pi-tableWrap">
          <table className="pi-table">
            <thead>
              <tr>
                <th>Id Produk</th>
                <th>Nama Produk</th>
                <th>Sumber</th>
                <th>Keterangan sumber</th>
                <th>Pabrik</th>
                <th>Tanggal Masuk</th>
                <th>Batch</th>
                <th>Stok Masuk</th>
                <th>Tanggal Kadaluarsa</th>
                <th>Keterangan</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="pi-center">
                    Memuat...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="pi-center">
                    Belum ada data.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => {
                  const p = r.product || {};
                  return (
                    <tr key={r.id}>
                      <td className="pi-mono">{p.id ?? r.product_id ?? "-"}</td>
                      <td className="pi-strong">{p.name ?? p.nama_produk ?? p.nama ?? "-"}</td>
                      <td>
                        <span className={`pi-badge ${r.source === "transfer_masuk" ? "warn" : "ok"}`}>
                          {sourceLabel(r.source)}
                        </span>
                      </td>
                      <td>{r.source_note ?? "-"}</td>
                      <td>{r.pabrik ?? "-"}</td>
                      <td>{fmtDate(r.tanggal_masuk)}</td>
                      <td className="pi-mono">{r.batch ?? "-"}</td>
                      <td className="pi-mono">{r.stok_masuk ?? "-"}</td>
                      <td>{fmtDate(r.tanggal_kadaluarsa)}</td>
                      <td>{r.keterangan ?? "-"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pi-pager">
          <button
            className="pi-btn pi-btn-light"
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => load(page - 1)}
          >
            Prev
          </button>

          <div className="pi-pagerInfo">
            Halaman <b>{page}</b> / <b>{lastPage}</b>
          </div>

          <button
            className="pi-btn pi-btn-light"
            type="button"
            disabled={page >= lastPage || loading}
            onClick={() => load(page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
